import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  ArrowRight,
  Check,
  Building2,
  User,
  MapPin,
  FileText,
  Euro,
  Settings,
  Eye,
  Save,
  Download,
  Plus,
  Trash2,
  Loader2,
  ClipboardList,
  Briefcase,
  Home,
  HelpCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Étapes du wizard
const STEPS = [
  { id: 'client-type', title: 'Type de client', icon: User },
  { id: 'client-info', title: 'Informations client', icon: Building2 },
  { id: 'mission', title: 'Mission / Projet', icon: ClipboardList },
  { id: 'prestations', title: 'Prestations', icon: Briefcase },
  { id: 'frais', title: 'Frais & Options', icon: Euro },
  { id: 'preview', title: 'Prévisualisation', icon: Eye },
];

// Composant Tooltip d'aide
const HelpTooltip = ({ text }) => (
  <div className="group relative inline-block ml-2">
    <HelpCircle className="w-4 h-4 text-slate-500 cursor-help" />
    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
      {text}
    </div>
  </div>
);

const QuoteWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [templates, setTemplates] = useState({ prestations: [], materiel: [], frais: [] });
  
  // État du devis
  const [quoteData, setQuoteData] = useState({
    quote_type: 'pre_devis',
    client: {
      type: 'prive',
      name: '',
      company: '',
      address: '',
      email: '',
      phone: '',
      siret: ''
    },
    mission: {
      title: '',
      description: '',
      site_address: '',
      establishment_type: '',
      start_date: '',
      end_date: '',
      context_notes: ''
    },
    sections: [
      { category: 'prestations', title: 'Prestations', lines: [], subtotal_ht: 0 },
      { category: 'materiel', title: 'Matériel', lines: [], subtotal_ht: 0 },
      { category: 'frais', title: 'Frais', lines: [], subtotal_ht: 0 }
    ],
    options: [
      { name: 'Journée supplémentaire', price_ht: 800 },
      { name: 'Soirée supplémentaire après journée', price_ht: 600 },
      { name: 'Intervention soirée seule', price_ht: 800 }
    ],
    clauses: {
      acompte_percent: 50,
      zero_commission: true,
      meteo_clause: true,
      custom_clauses: []
    },
    internal_notes: ''
  });

  // Charger les templates
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const token = localStorage.getItem('tkale_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [prestRes, materielRes, fraisRes] = await Promise.all([
        axios.get(`${API}/quotes/templates/prestations`, { headers }),
        axios.get(`${API}/quotes/templates/materiel`, { headers }),
        axios.get(`${API}/quotes/templates/frais`, { headers })
      ]);
      
      setTemplates({
        prestations: prestRes.data,
        materiel: materielRes.data,
        frais: fraisRes.data
      });
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  // Calcul des totaux
  const calculateTotals = () => {
    let subtotalPrestations = 0;
    let subtotalMateriel = 0;
    let subtotalFrais = 0;
    let fraisDeductibles = 0;

    quoteData.sections.forEach(section => {
      const sectionTotal = section.lines.reduce((sum, line) => sum + (line.total_ht || 0), 0);
      
      if (section.category === 'prestations') subtotalPrestations = sectionTotal;
      if (section.category === 'materiel') subtotalMateriel = sectionTotal;
      if (section.category === 'frais') {
        subtotalFrais = sectionTotal;
        fraisDeductibles = section.lines
          .filter(l => l.deductible)
          .reduce((sum, l) => sum + (l.total_ht || 0), 0);
      }
    });

    const totalMin = subtotalPrestations + subtotalMateriel + (subtotalFrais - fraisDeductibles);
    const totalMax = subtotalPrestations + subtotalMateriel + subtotalFrais;

    return {
      subtotalPrestations,
      subtotalMateriel,
      subtotalFrais,
      totalMin,
      totalMax,
      totalMinTTC: totalMin * 1.2,
      totalMaxTTC: totalMax * 1.2
    };
  };

  // Mise à jour des données
  const updateClient = (field, value) => {
    setQuoteData(prev => ({
      ...prev,
      client: { ...prev.client, [field]: value }
    }));
  };

  const updateMission = (field, value) => {
    setQuoteData(prev => ({
      ...prev,
      mission: { ...prev.mission, [field]: value }
    }));
  };

  const addLine = (sectionCategory, template = null) => {
    const newLine = template ? {
      description: template.name,
      quantity: 1,
      unit: template.unit,
      unit_price_ht: template.price_ht,
      total_ht: template.price_ht,
      deductible: template.deductible || false,
      provision_capped: template.provision_capped || false
    } : {
      description: '',
      quantity: 1,
      unit: 'forfait',
      unit_price_ht: 0,
      total_ht: 0,
      deductible: false,
      provision_capped: false
    };

    setQuoteData(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.category === sectionCategory 
          ? { ...s, lines: [...s.lines, newLine] }
          : s
      )
    }));
  };

  const updateLine = (sectionCategory, lineIndex, field, value) => {
    setQuoteData(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.category !== sectionCategory) return s;
        
        const newLines = [...s.lines];
        newLines[lineIndex] = { ...newLines[lineIndex], [field]: value };
        
        // Recalculer le total si quantité ou prix modifié
        if (field === 'quantity' || field === 'unit_price_ht') {
          newLines[lineIndex].total_ht = 
            newLines[lineIndex].quantity * newLines[lineIndex].unit_price_ht;
        }
        
        return { ...s, lines: newLines };
      })
    }));
  };

  const removeLine = (sectionCategory, lineIndex) => {
    setQuoteData(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.category === sectionCategory 
          ? { ...s, lines: s.lines.filter((_, i) => i !== lineIndex) }
          : s
      )
    }));
  };

  // Navigation
  const nextStep = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (index) => {
    setCurrentStep(index);
  };

  // Sauvegarde
  const handleSave = async () => {
    if (!quoteData.client.name || !quoteData.mission.title) {
      toast.error('Veuillez remplir le nom du client et le titre de la mission');
      return;
    }

    setIsSaving(true);
    try {
      const token = localStorage.getItem('tkale_token');
      const response = await axios.post(`${API}/quotes`, quoteData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Devis ${response.data.quote_number} créé !`);
      navigate(`/quotes/${response.data.quote_id}`);
    } catch (error) {
      console.error('Error saving quote:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const totals = calculateTotals();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="quote-wizard">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate(-1)}
                className="text-slate-400 hover:text-white"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white">Nouveau Devis</h1>
                <p className="text-sm text-slate-400">
                  {quoteData.quote_type === 'pre_devis' ? 'Pré-devis / Enveloppe budgétaire' : 'Devis détaillé contractuel'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge 
                variant="outline" 
                className={`${
                  quoteData.quote_type === 'pre_devis' 
                    ? 'border-amber-500/50 text-amber-400' 
                    : 'border-green-500/50 text-green-400'
                }`}
              >
                {quoteData.quote_type === 'pre_devis' ? 'Pré-devis' : 'Devis détaillé'}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="border-b border-slate-700/50 bg-slate-800/30">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <div 
                key={step.id}
                className={`flex items-center cursor-pointer ${index <= currentStep ? 'opacity-100' : 'opacity-50'}`}
                onClick={() => goToStep(index)}
              >
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center transition-colors
                  ${index < currentStep ? 'bg-green-600' : index === currentStep ? 'bg-cyan-600' : 'bg-slate-700'}
                `}>
                  {index < currentStep ? (
                    <Check className="w-5 h-5 text-white" />
                  ) : (
                    <step.icon className="w-5 h-5 text-white" />
                  )}
                </div>
                <span className={`ml-2 text-sm hidden md:block ${index === currentStep ? 'text-white font-medium' : 'text-slate-400'}`}>
                  {step.title}
                </span>
                {index < STEPS.length - 1 && (
                  <div className={`w-8 lg:w-16 h-0.5 mx-2 ${index < currentStep ? 'bg-green-600' : 'bg-slate-700'}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          
          {/* Étape 1: Type de client */}
          {currentStep === 0 && (
            <motion.div
              key="client-type"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-white mb-2">Quel type de client ?</h2>
                <p className="text-slate-400">Le parcours s'adapte selon votre profil</p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                <Card 
                  className={`cursor-pointer transition-all ${
                    quoteData.client.type === 'pro' 
                      ? 'bg-blue-900/50 border-blue-500 border-2' 
                      : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                  }`}
                  onClick={() => updateClient('type', 'pro')}
                  data-testid="client-type-pro"
                >
                  <CardContent className="p-8 text-center">
                    <Building2 className={`w-16 h-16 mx-auto mb-4 ${quoteData.client.type === 'pro' ? 'text-blue-400' : 'text-slate-400'}`} />
                    <h3 className="text-xl font-bold text-white mb-2">Professionnel</h3>
                    <p className="text-slate-400 text-sm">
                      Entreprise, ERP, établissement avec activité commerciale
                    </p>
                    <ul className="mt-4 text-left text-sm text-slate-500 space-y-1">
                      <li>• SIRET / SIREN requis</li>
                      <li>• Facturation avec TVA</li>
                      <li>• Accès aux options avancées</li>
                    </ul>
                  </CardContent>
                </Card>

                <Card 
                  className={`cursor-pointer transition-all ${
                    quoteData.client.type === 'prive' 
                      ? 'bg-purple-900/50 border-purple-500 border-2' 
                      : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                  }`}
                  onClick={() => updateClient('type', 'prive')}
                  data-testid="client-type-prive"
                >
                  <CardContent className="p-8 text-center">
                    <Home className={`w-16 h-16 mx-auto mb-4 ${quoteData.client.type === 'prive' ? 'text-purple-400' : 'text-slate-400'}`} />
                    <h3 className="text-xl font-bold text-white mb-2">Particulier</h3>
                    <p className="text-slate-400 text-sm">
                      Personne physique, copropriété, association
                    </p>
                    <ul className="mt-4 text-left text-sm text-slate-500 space-y-1">
                      <li>• Parcours simplifié</li>
                      <li>• Aide à chaque étape</li>
                      <li>• Devis adapté</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              {/* Type de devis */}
              <div className="max-w-3xl mx-auto mt-8">
                <Label className="text-slate-300 mb-3 block">Type de document</Label>
                <div className="grid grid-cols-2 gap-4">
                  <Card 
                    className={`cursor-pointer transition-all ${
                      quoteData.quote_type === 'pre_devis' 
                        ? 'bg-amber-900/30 border-amber-500' 
                        : 'bg-slate-800/50 border-slate-700'
                    }`}
                    onClick={() => setQuoteData(prev => ({ ...prev, quote_type: 'pre_devis' }))}
                  >
                    <CardContent className="p-4">
                      <h4 className="font-medium text-white">Pré-devis</h4>
                      <p className="text-xs text-slate-400">Enveloppe budgétaire, envoi rapide</p>
                    </CardContent>
                  </Card>
                  <Card 
                    className={`cursor-pointer transition-all ${
                      quoteData.quote_type === 'devis_detaille' 
                        ? 'bg-green-900/30 border-green-500' 
                        : 'bg-slate-800/50 border-slate-700'
                    }`}
                    onClick={() => setQuoteData(prev => ({ ...prev, quote_type: 'devis_detaille' }))}
                  >
                    <CardContent className="p-4">
                      <h4 className="font-medium text-white">Devis détaillé</h4>
                      <p className="text-xs text-slate-400">Contractuel, après accord commercial</p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </motion.div>
          )}

          {/* Étape 2: Informations client */}
          {currentStep === 1 && (
            <motion.div
              key="client-info"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    {quoteData.client.type === 'pro' ? <Building2 className="w-5 h-5 text-blue-400" /> : <User className="w-5 h-5 text-purple-400" />}
                    Informations {quoteData.client.type === 'pro' ? 'entreprise' : 'client'}
                  </CardTitle>
                  <CardDescription>Ces informations apparaîtront sur le devis</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">
                        {quoteData.client.type === 'pro' ? 'Raison sociale *' : 'Nom complet *'}
                      </Label>
                      <Input
                        value={quoteData.client.name}
                        onChange={(e) => updateClient('name', e.target.value)}
                        placeholder={quoteData.client.type === 'pro' ? 'SARL Example' : 'M. / Mme Dupont'}
                        className="bg-slate-900 border-slate-600 text-white"
                        data-testid="client-name"
                      />
                    </div>
                    {quoteData.client.type === 'pro' && (
                      <div>
                        <Label className="text-slate-300">
                          Contact
                          <HelpTooltip text="Personne à contacter" />
                        </Label>
                        <Input
                          value={quoteData.client.company}
                          onChange={(e) => updateClient('company', e.target.value)}
                          placeholder="M. Martin, Directeur"
                          className="bg-slate-900 border-slate-600 text-white"
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <Label className="text-slate-300">Adresse *</Label>
                    <Textarea
                      value={quoteData.client.address}
                      onChange={(e) => updateClient('address', e.target.value)}
                      placeholder="Adresse complète..."
                      className="bg-slate-900 border-slate-600 text-white"
                      rows={2}
                      data-testid="client-address"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Email *</Label>
                      <Input
                        type="email"
                        value={quoteData.client.email}
                        onChange={(e) => updateClient('email', e.target.value)}
                        placeholder="contact@example.com"
                        className="bg-slate-900 border-slate-600 text-white"
                        data-testid="client-email"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Téléphone</Label>
                      <Input
                        value={quoteData.client.phone}
                        onChange={(e) => updateClient('phone', e.target.value)}
                        placeholder="+33 6 12 34 56 78"
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                  </div>

                  {quoteData.client.type === 'pro' && (
                    <div>
                      <Label className="text-slate-300">
                        SIRET
                        <HelpTooltip text="Numéro SIRET à 14 chiffres" />
                      </Label>
                      <Input
                        value={quoteData.client.siret}
                        onChange={(e) => updateClient('siret', e.target.value)}
                        placeholder="123 456 789 00012"
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Étape 3: Mission */}
          {currentStep === 2 && (
            <motion.div
              key="mission"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-cyan-400" />
                    Description de la mission
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Titre de la mission *</Label>
                    <Input
                      value={quoteData.mission.title}
                      onChange={(e) => updateMission('title', e.target.value)}
                      placeholder="Ex: Mission acoustique & optimisation sonore"
                      className="bg-slate-900 border-slate-600 text-white"
                      data-testid="mission-title"
                    />
                  </div>

                  <div>
                    <Label className="text-slate-300">
                      Description du besoin
                      <HelpTooltip text="Décrivez le contexte et les objectifs" />
                    </Label>
                    <Textarea
                      value={quoteData.mission.description}
                      onChange={(e) => updateMission('description', e.target.value)}
                      placeholder="Décrivez le contexte, les contraintes, les objectifs..."
                      className="bg-slate-900 border-slate-600 text-white"
                      rows={4}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Type d'établissement</Label>
                      <Input
                        value={quoteData.mission.establishment_type}
                        onChange={(e) => updateMission('establishment_type', e.target.value)}
                        placeholder="Ex: Bar, Restaurant, Discothèque..."
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Adresse du site (si différente)</Label>
                      <Input
                        value={quoteData.mission.site_address}
                        onChange={(e) => updateMission('site_address', e.target.value)}
                        placeholder="Adresse d'intervention"
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-300">Date de début estimée</Label>
                      <Input
                        type="date"
                        value={quoteData.mission.start_date}
                        onChange={(e) => updateMission('start_date', e.target.value)}
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Date de fin estimée</Label>
                      <Input
                        type="date"
                        value={quoteData.mission.end_date}
                        onChange={(e) => updateMission('end_date', e.target.value)}
                        className="bg-slate-900 border-slate-600 text-white"
                      />
                    </div>
                  </div>

                  {quoteData.quote_type === 'pre_devis' && (
                    <div>
                      <Label className="text-slate-300">
                        Notes de contexte (pour le pré-devis)
                        <HelpTooltip text="Explications simples pour le client" />
                      </Label>
                      <Textarea
                        value={quoteData.mission.context_notes}
                        onChange={(e) => updateMission('context_notes', e.target.value)}
                        placeholder="Ex: Déplacement conséquent, site multi-zones, validation en conditions réelles..."
                        className="bg-slate-900 border-slate-600 text-white"
                        rows={3}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Étape 4: Prestations & Matériel */}
          {currentStep === 3 && (
            <motion.div
              key="prestations"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* Prestations */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-blue-400" />
                      Prestations d'ingénierie
                    </CardTitle>
                    <div className="flex gap-2">
                      <select 
                        className="bg-slate-700 text-white text-sm rounded px-2 py-1 border-slate-600"
                        onChange={(e) => {
                          const template = templates.prestations.find(t => t.id === e.target.value);
                          if (template) addLine('prestations', template);
                          e.target.value = '';
                        }}
                      >
                        <option value="">+ Ajouter prestation type</option>
                        {templates.prestations.map(t => (
                          <option key={t.id} value={t.id}>{t.name} ({t.price_ht}€)</option>
                        ))}
                      </select>
                      <Button size="sm" variant="outline" onClick={() => addLine('prestations')} className="border-slate-600 text-slate-300">
                        <Plus className="w-4 h-4 mr-1" /> Ligne vide
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {quoteData.sections.find(s => s.category === 'prestations')?.lines.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">Aucune prestation ajoutée</p>
                  ) : (
                    <div className="space-y-3">
                      {quoteData.sections.find(s => s.category === 'prestations')?.lines.map((line, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-900/50 p-3 rounded-lg">
                          <div className="col-span-5">
                            <Input
                              value={line.description}
                              onChange={(e) => updateLine('prestations', i, 'description', e.target.value)}
                              placeholder="Description"
                              className="bg-slate-800 border-slate-600 text-white text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              type="number"
                              value={line.quantity}
                              onChange={(e) => updateLine('prestations', i, 'quantity', parseFloat(e.target.value) || 0)}
                              className="bg-slate-800 border-slate-600 text-white text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              type="number"
                              value={line.unit_price_ht}
                              onChange={(e) => updateLine('prestations', i, 'unit_price_ht', parseFloat(e.target.value) || 0)}
                              className="bg-slate-800 border-slate-600 text-white text-sm"
                            />
                          </div>
                          <div className="col-span-2 text-right">
                            <span className="text-cyan-400 font-medium">{line.total_ht?.toFixed(2)} €</span>
                          </div>
                          <div className="col-span-1 text-right">
                            <Button size="sm" variant="ghost" onClick={() => removeLine('prestations', i)} className="text-red-400 hover:text-red-300">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Matériel */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Settings className="w-5 h-5 text-green-400" />
                      Matériel
                    </CardTitle>
                    <div className="flex gap-2">
                      <select 
                        className="bg-slate-700 text-white text-sm rounded px-2 py-1 border-slate-600"
                        onChange={(e) => {
                          const template = templates.materiel.find(t => t.id === e.target.value);
                          if (template) addLine('materiel', template);
                          e.target.value = '';
                        }}
                      >
                        <option value="">+ Ajouter matériel type</option>
                        {templates.materiel.map(t => (
                          <option key={t.id} value={t.id}>{t.name} ({t.price_ht}€)</option>
                        ))}
                      </select>
                      <Button size="sm" variant="outline" onClick={() => addLine('materiel')} className="border-slate-600 text-slate-300">
                        <Plus className="w-4 h-4 mr-1" /> Ligne vide
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {quoteData.sections.find(s => s.category === 'materiel')?.lines.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">Aucun matériel ajouté</p>
                  ) : (
                    <div className="space-y-3">
                      {quoteData.sections.find(s => s.category === 'materiel')?.lines.map((line, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-900/50 p-3 rounded-lg">
                          <div className="col-span-5">
                            <Input
                              value={line.description}
                              onChange={(e) => updateLine('materiel', i, 'description', e.target.value)}
                              placeholder="Description"
                              className="bg-slate-800 border-slate-600 text-white text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              type="number"
                              value={line.quantity}
                              onChange={(e) => updateLine('materiel', i, 'quantity', parseFloat(e.target.value) || 0)}
                              className="bg-slate-800 border-slate-600 text-white text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              type="number"
                              value={line.unit_price_ht}
                              onChange={(e) => updateLine('materiel', i, 'unit_price_ht', parseFloat(e.target.value) || 0)}
                              className="bg-slate-800 border-slate-600 text-white text-sm"
                            />
                          </div>
                          <div className="col-span-2 text-right">
                            <span className="text-green-400 font-medium">{line.total_ht?.toFixed(2)} €</span>
                          </div>
                          <div className="col-span-1 text-right">
                            <Button size="sm" variant="ghost" onClick={() => removeLine('materiel', i)} className="text-red-400 hover:text-red-300">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Étape 5: Frais & Options */}
          {currentStep === 4 && (
            <motion.div
              key="frais"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* Frais */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Euro className="w-5 h-5 text-amber-400" />
                      Frais
                    </CardTitle>
                    <div className="flex gap-2">
                      <select 
                        className="bg-slate-700 text-white text-sm rounded px-2 py-1 border-slate-600"
                        onChange={(e) => {
                          const template = templates.frais.find(t => t.id === e.target.value);
                          if (template) addLine('frais', template);
                          e.target.value = '';
                        }}
                      >
                        <option value="">+ Ajouter frais type</option>
                        {templates.frais.map(t => (
                          <option key={t.id} value={t.id}>{t.name} ({t.price_ht}€){t.deductible ? ' - Déductible' : ''}</option>
                        ))}
                      </select>
                      <Button size="sm" variant="outline" onClick={() => addLine('frais')} className="border-slate-600 text-slate-300">
                        <Plus className="w-4 h-4 mr-1" /> Ligne vide
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {quoteData.sections.find(s => s.category === 'frais')?.lines.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">Aucun frais ajouté</p>
                  ) : (
                    <div className="space-y-3">
                      {quoteData.sections.find(s => s.category === 'frais')?.lines.map((line, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-900/50 p-3 rounded-lg">
                          <div className="col-span-4">
                            <Input
                              value={line.description}
                              onChange={(e) => updateLine('frais', i, 'description', e.target.value)}
                              placeholder="Description"
                              className="bg-slate-800 border-slate-600 text-white text-sm"
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              type="number"
                              value={line.total_ht}
                              onChange={(e) => {
                                updateLine('frais', i, 'total_ht', parseFloat(e.target.value) || 0);
                                updateLine('frais', i, 'unit_price_ht', parseFloat(e.target.value) || 0);
                              }}
                              className="bg-slate-800 border-slate-600 text-white text-sm"
                            />
                          </div>
                          <div className="col-span-2 flex items-center gap-2">
                            <Checkbox 
                              checked={line.deductible}
                              onCheckedChange={(checked) => updateLine('frais', i, 'deductible', checked)}
                            />
                            <span className="text-xs text-slate-400">Déductible</span>
                          </div>
                          <div className="col-span-2 flex items-center gap-2">
                            <Checkbox 
                              checked={line.provision_capped}
                              onCheckedChange={(checked) => updateLine('frais', i, 'provision_capped', checked)}
                            />
                            <span className="text-xs text-slate-400">Plafonné</span>
                          </div>
                          <div className="col-span-1 text-right">
                            <span className={`font-medium ${line.deductible ? 'text-slate-400' : 'text-amber-400'}`}>
                              {line.total_ht?.toFixed(2)} €
                            </span>
                          </div>
                          <div className="col-span-1 text-right">
                            <Button size="sm" variant="ghost" onClick={() => removeLine('frais', i)} className="text-red-400 hover:text-red-300">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Clauses */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Clauses & Conditions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-3">
                      <Checkbox 
                        checked={quoteData.clauses.zero_commission}
                        onCheckedChange={(checked) => setQuoteData(prev => ({
                          ...prev, clauses: { ...prev.clauses, zero_commission: checked }
                        }))}
                      />
                      <Label className="text-slate-300">Clause zéro commission</Label>
                    </div>
                    <div className="flex items-center gap-3">
                      <Checkbox 
                        checked={quoteData.clauses.meteo_clause}
                        onCheckedChange={(checked) => setQuoteData(prev => ({
                          ...prev, clauses: { ...prev.clauses, meteo_clause: checked }
                        }))}
                      />
                      <Label className="text-slate-300">Clause météo</Label>
                    </div>
                    <div className="flex items-center gap-3">
                      <Label className="text-slate-300">Acompte:</Label>
                      <Input
                        type="number"
                        value={quoteData.clauses.acompte_percent}
                        onChange={(e) => setQuoteData(prev => ({
                          ...prev, clauses: { ...prev.clauses, acompte_percent: parseInt(e.target.value) || 50 }
                        }))}
                        className="bg-slate-800 border-slate-600 text-white w-20"
                      />
                      <span className="text-slate-400">%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Étape 6: Prévisualisation */}
          {currentStep === 5 && (
            <motion.div
              key="preview"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Eye className="w-5 h-5 text-cyan-400" />
                    Prévisualisation du {quoteData.quote_type === 'pre_devis' ? 'Pré-Devis' : 'Devis Détaillé'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Client */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-medium text-slate-400 mb-2">Client</h4>
                      <p className="text-white font-medium">{quoteData.client.name || '—'}</p>
                      <p className="text-slate-400 text-sm">{quoteData.client.address || '—'}</p>
                      <p className="text-slate-400 text-sm">{quoteData.client.email}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-slate-400 mb-2">Mission</h4>
                      <p className="text-white font-medium">{quoteData.mission.title || '—'}</p>
                      <p className="text-slate-400 text-sm">{quoteData.mission.establishment_type}</p>
                      {quoteData.mission.start_date && (
                        <p className="text-slate-400 text-sm">
                          Du {quoteData.mission.start_date} au {quoteData.mission.end_date}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Totaux */}
                  <div className="bg-slate-900/50 rounded-lg p-6">
                    <h4 className="text-lg font-medium text-white mb-4">Récapitulatif</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Prestations</span>
                        <span className="text-white">{totals.subtotalPrestations.toFixed(2)} € HT</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Matériel</span>
                        <span className="text-white">{totals.subtotalMateriel.toFixed(2)} € HT</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Frais</span>
                        <span className="text-white">{totals.subtotalFrais.toFixed(2)} € HT</span>
                      </div>
                      <hr className="border-slate-700 my-3" />
                      <div className="flex justify-between text-lg">
                        <span className="text-white font-medium">Total HT</span>
                        <span className="text-cyan-400 font-bold">
                          {totals.totalMin === totals.totalMax 
                            ? `${totals.totalMax.toFixed(2)} €`
                            : `${totals.totalMin.toFixed(2)} à ${totals.totalMax.toFixed(2)} €`
                          }
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-400">Total TTC (TVA 20%)</span>
                        <span className="text-slate-300">
                          {totals.totalMinTTC === totals.totalMaxTTC 
                            ? `${totals.totalMaxTTC.toFixed(2)} €`
                            : `${totals.totalMinTTC.toFixed(2)} à ${totals.totalMaxTTC.toFixed(2)} €`
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Clauses */}
                  <div className="text-sm text-slate-400">
                    <p>• Acompte {quoteData.clauses.acompte_percent}% à la commande</p>
                    {quoteData.clauses.zero_commission && <p>• Clause zéro commission</p>}
                    {quoteData.clauses.meteo_clause && <p>• Mesures extérieures sous réserve conditions météo</p>}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

        </AnimatePresence>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-700">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0}
            className="border-slate-600 text-slate-300"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Précédent
          </Button>

          <div className="flex gap-3">
            {currentStep === STEPS.length - 1 ? (
              <>
                <Button
                  variant="outline"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="border-slate-600 text-slate-300"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer brouillon
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="bg-cyan-600 hover:bg-cyan-500"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Créer le devis
                </Button>
              </>
            ) : (
              <Button onClick={nextStep} className="bg-cyan-600 hover:bg-cyan-500">
                Suivant
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default QuoteWizard;
